import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './AuthPage.css';

const AuthPage = () => {
  const { login, register, API_URL } = useAuth();

  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('token');
  const resetId = urlParams.get('id');
  const isResetMode = urlParams.get('reset') === 'true' && resetToken && resetId;

  const verifyToken = urlParams.get('token');
  const isVerifyMode = urlParams.get('verify') === 'true' && verifyToken;

  const confirmDeleteToken = urlParams.get('token');
  const isConfirmDeleteMode = urlParams.get('confirm-delete') === 'true' && confirmDeleteToken;

  const initialMode = isResetMode ? 'reset-password' : isConfirmDeleteMode ? 'confirm-delete' : 'login';
  const [mode, setMode] = useState(initialMode); // 'login' | 'register' | 'forgot-password' | 'reset-password' | 'confirm-delete'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [deleteData, setDeleteData] = useState(null);

  useEffect(() => {
    if (isVerifyMode) {
      setLoading(true);
      fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verifyToken })
      })
      .then(res => res.json().then(data => ({ status: res.status, data })))
      .then(({ status, data }) => {
        if (status === 200) {
          setSuccessMsg(data.message || 'E-mail verificado com sucesso!');
        } else {
          setError(data.message || 'Erro ao verificar e-mail.');
        }
      })
      .catch(err => setError('Erro de conexão ao verificar e-mail.'))
      .finally(() => {
        setLoading(false);
        window.history.replaceState({}, document.title, window.location.pathname);
      });
    }
  }, [isVerifyMode, verifyToken, API_URL]);

  useEffect(() => {
    if (isConfirmDeleteMode) {
      setLoading(true);
      fetch(`${API_URL}/api/auth/validate-delete-token?token=${confirmDeleteToken}`)
        .then(res => res.json().then(data => ({ status: res.status, data })))
        .then(({ status, data }) => {
          if (status === 200 && data.valid) {
            setDeleteData(data);
          } else {
            setError(data.message || 'O link de exclusão é inválido ou expirou.');
          }
        })
        .catch(err => setError('Erro de conexão ao validar token de exclusão.'))
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isConfirmDeleteMode, confirmDeleteToken, API_URL]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setSuccessMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (mode === 'register' || mode === 'reset-password') {
      if (form.password !== form.confirmPassword) {
        return setError('As senhas não coincidem');
      }
      if (form.password.length < 6) {
        return setError('A senha deve ter no mínimo 6 caracteres');
      }
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else if (mode === 'register') {
        const data = await register(form.name, form.email, form.password);
        if (data.requireVerification) {
          setSuccessMsg(data.message);
          setForm({ name: '', email: '', password: '', confirmPassword: '' });
          setTimeout(() => switchMode('login'), 5000);
        }
      } else if (mode === 'forgot-password') {
        const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Erro ao solicitar recuperação');
        setSuccessMsg(data.message || 'Link enviado com sucesso! Verifique seu email.');
      } else if (mode === 'reset-password') {
        const res = await fetch(`${API_URL}/api/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: resetId,
            token: resetToken,
            newPassword: form.password
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Erro ao redefinir senha');
        setSuccessMsg('Senha alterada com sucesso! Você já pode fazer login.');
        // Remove os parametros da url para limpar
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => switchMode('login'), 3000);
      } else if (mode === 'confirm-delete') {
        const res = await fetch(`${API_URL}/api/auth/confirm-delete-account`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: confirmDeleteToken }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Erro ao excluir conta');
        setSuccessMsg(data.message || 'Conta excluída com sucesso.');
        setDeleteData(null);
        // Garante que a sessão local seja apagada no dispositivo
        localStorage.removeItem('wf_token');
        localStorage.removeItem('wf_user');
        localStorage.removeItem('wf_accounts');
        localStorage.removeItem('wf_vision');

        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccessMsg('');
    setForm({ name: '', email: '', password: '', confirmPassword: '' });
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
          {/* Logo e título */}
        <div className="auth-header">
          <div className="auth-logo" style={{ background: 'transparent', boxShadow: 'none' }}>
            <img src="/favicon.png" alt="Logo" className="auth-logo-img" style={{ width: '64px', height: '64px', borderRadius: '14px', objectFit: 'cover' }} />
          </div>
          <h1 className="auth-title">Walco Finance</h1>
          <p className="auth-subtitle">
            {mode === 'login' ? 'Entre na sua conta' : 
             mode === 'register' ? 'Crie sua conta grátis' : 
             mode === 'forgot-password' ? 'Recuperar Senha' : 
             mode === 'confirm-delete' ? 'Excluir Conta' : 'Redefinir Senha'}
          </p>
        </div>

        {/* Tabs - Only show on login or register */}
        {(mode === 'login' || mode === 'register') && (
          <div className="auth-tabs">
            <button
              className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Entrar
            </button>
            <button
              className={`auth-tab ${mode === 'register' ? 'active' : ''}`}
              onClick={() => switchMode('register')}
            >
              Cadastrar
            </button>
          </div>
        )}

        {/* Formulário */}
        <form className="auth-form" onSubmit={handleSubmit}>
          {mode === 'register' && (
            <div className="auth-field">
              <label htmlFor="name">Nome completo</label>
              <div className="auth-input-wrapper">
                <span className="auth-input-icon">👤</span>
                <input
                  id="name"
                  type="text"
                  name="name"
                  placeholder="Seu nome"
                  value={form.name}
                  onChange={handleChange}
                  autoComplete="name"
                />
              </div>
            </div>
          )}

          {(mode === 'login' || mode === 'register' || mode === 'forgot-password') && (
            <div className="auth-field">
              <label htmlFor="email">Email</label>
              <div className="auth-input-wrapper">
                <span className="auth-input-icon">✉️</span>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="seu@email.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />
              </div>
            </div>
          )}

          {(mode === 'login' || mode === 'register' || mode === 'reset-password') && (
            <div className="auth-field">
              <label htmlFor="password">
                {mode === 'reset-password' ? 'Nova Senha' : 'Senha'}
              </label>
              <div className="auth-input-wrapper">
                <span className="auth-input-icon">🔒</span>
                <input
                  id="password"
                  type="password"
                  name="password"
                  placeholder={mode === 'login' ? 'Sua senha' : 'Mínimo 6 caracteres'}
                  value={form.password}
                  onChange={handleChange}
                  required
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
              </div>
            </div>
          )}

          {(mode === 'register' || mode === 'reset-password') && (
            <div className="auth-field">
              <label htmlFor="confirmPassword">Confirmar senha</label>
              <div className="auth-input-wrapper">
                <span className="auth-input-icon">🔒</span>
                <input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  placeholder="Repita sua senha"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="auth-error">
              <span>⚠️</span> {error}
            </div>
          )}

          {successMsg && (
            <div className="auth-success" style={{ background: '#dcfce7', color: '#166534', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>✅</span> {successMsg}
            </div>
          )}

          {mode === 'confirm-delete' && deleteData && !successMsg && (
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <p style={{ color: '#475569', marginBottom: '16px' }}>
                Você está prestes a excluir a conta associada ao e-mail:<br/>
                <strong>{deleteData.email}</strong>
              </p>
              <p style={{ color: '#ef4444', fontWeight: 'bold' }}>
                Esta ação é definitiva e não pode ser desfeita. Todos os seus dados serão apagados para sempre.
              </p>
              <p style={{ color: '#475569', marginTop: '16px' }}>
                Tem certeza absoluta?
              </p>
            </div>
          )}

          <button
            type="submit"
            className="auth-btn-primary"
            disabled={loading || (mode === 'confirm-delete' && !deleteData && !successMsg)}
            style={mode === 'confirm-delete' ? { background: '#ef4444' } : {}}
          >
            {loading ? (
              <span className="auth-spinner">⟳</span>
            ) : mode === 'login' ? (
              'Entrar'
            ) : mode === 'register' ? (
              'Criar conta'
            ) : mode === 'forgot-password' ? (
              'Enviar Link de Recuperação'
            ) : mode === 'confirm-delete' ? (
              '👉 Confirmar exclusão definitiva'
            ) : (
              'Salvar Nova Senha'
            )}
          </button>
        </form>

        <div className="auth-footer" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {mode === 'login' && (
            <button className="auth-link" style={{ alignSelf: 'center', fontSize: '14px' }} onClick={() => switchMode('forgot-password')}>
              Esqueceu sua senha?
            </button>
          )}

          <div>
            {mode === 'login' ? (
              <p>
                Não tem conta?{' '}
                <button className="auth-link" onClick={() => switchMode('register')}>
                  Cadastre-se grátis
                </button>
              </p>
            ) : mode === 'register' ? (
              <p>
                Já tem conta?{' '}
                <button className="auth-link" onClick={() => switchMode('login')}>
                  Faça login
                </button>
              </p>
            ) : (
              <p>
                Lembrou a senha?{' '}
                <button className="auth-link" onClick={() => switchMode('login')}>
                  Voltar ao login
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
